import {
  Button,
  ModalBody,
  ModalFooter,
  Flex,
  Input,
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage
} from '@chakra-ui/react'
import { useMutation } from '@tanstack/react-query'
import { Fragment, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { assignNamespaceTo } from '../../../../../api/namespaces'
import { clientSignUp as signUp } from '../../../../../api/users'
import { Alert } from '../../../../../components/Feedback'


interface StepCreateAndAssignUserProps {
  namespaceId: string
  close: () => void
}

export function StepCreateAndAssignUser({ namespaceId, close }: StepCreateAndAssignUserProps) {
  const { t } = useTranslation('translation', { keyPrefix: 'namespaces.create.steps.create_assign_user' })

  const [ fullname, setFullname ] = useState('')
  const [ isFullnameTouched, setIsFullnameTouched ] = useState(false)

  const [ email, setEmail ] = useState('')
  const [ isEmailTouched, setIsEmailTouched ] = useState(false)

  const [ password, setPassword ] = useState('')
  const [ isPasswordTouched, setIsPasswordTouched ] = useState(false)

  const fullNameError = useMemo(() => {
    const schema = z.string()
      .min(1, t('name.errors.required'))
      .max(64, t('name.errors.too_long', { max: 64 }))
    const result = schema.safeParse(fullname)
    if (!result.success) return result.error.errors[0].message
  }, [ fullname, t ])

  const emailError = useMemo(() => {
    const schema = z.string()
      .min(1, t('email.errors.required'))
      .email(t('email.errors.invalid_format'))
    const result = schema.safeParse(email)
    if (!result.success) return result.error.errors[0].message
  }, [ email, t ])

  const passwordError = useMemo(() => {
    const schema = z.string()
      .min(8, t('password.errors.too_short', { min: 8 }))
    const result = schema.safeParse(password)
    if (!result.success) return result.error.errors[0].message
  }, [ password, t ])

  const signupMutation = useMutation({
    mutationKey: [ 'users', 'signup' ],
    mutationFn: () => signUp(email, password, { fullname }),
    onSuccess: data => {
      assignMutation.mutate(data.user.id)
    }
  })

  const assignMutation = useMutation({
    mutationKey: [ 'namespaces', namespaceId, 'assign-to' ],
    mutationFn: (userId: string) => assignNamespaceTo({ namespaceId, userId }),
    onSuccess: _ => close()
  })

  return (
    <Fragment>
      <ModalBody as={Flex} direction="column" gap={8}>

        {signupMutation.isError && (
          <Alert status="error" description={t('alerts.signup')} />
        )}

        {assignMutation.isError && (
          <Alert status="error" description={t('alerts.assign')} />
        )}

        <FormControl isInvalid={isFullnameTouched && !!fullNameError} isRequired>
          <FormLabel>{t('full_name.label')}</FormLabel>
          <Input
            value={fullname}
            onChange={e => setFullname(e.target.value)}
            onBlur={() => setIsFullnameTouched(true)}
            placeholder={t('full_name.placeholder')}
          />
          {fullNameError && (
            <FormErrorMessage>{fullNameError}</FormErrorMessage>
          )}
          <FormHelperText>{t('full_name.description')}</FormHelperText>
        </FormControl>

        <FormControl isInvalid={isEmailTouched && !!emailError} isRequired>
          <FormLabel>{t('email.label')}</FormLabel>
          <Input
            value={email}
            onChange={e => setEmail(e.target.value)}
            onBlur={() => setIsEmailTouched(true)}
            placeholder={t('email.placeholder')}
          />
          {emailError && (
            <FormErrorMessage>{emailError}</FormErrorMessage>
          )}
          <FormHelperText>{t('email.description')}</FormHelperText>
        </FormControl>

        <FormControl isInvalid={isPasswordTouched && !!passwordError} isRequired>
          <FormLabel>{t('password.label')}</FormLabel>
          <Input
            value={password}
            onChange={e => setPassword(e.target.value)}
            onBlur={() => setIsPasswordTouched(true)}
            placeholder={t('password.placeholder')}
            type="password"
          />
          {passwordError && (
            <FormErrorMessage>{passwordError}</FormErrorMessage>
          )}
          <FormHelperText>{t('password.description')}</FormHelperText>
        </FormControl>

      </ModalBody>
      <ModalFooter gap={4}>
        <Button onClick={close} variant="ghost">
          {t('buttons.cancel')}
        </Button>
        <Button
          onClick={() => signupMutation.mutate()}
          isDisabled={!!fullNameError}
          isLoading={signupMutation.isPending || assignMutation.isPending}
          loadingText={t('buttons.create_assign')}
          colorScheme="blue"
        >
          {t('buttons.create_assign')}
        </Button>
      </ModalFooter>
    </Fragment>
  )
}
